import React, { useState } from 'react';
import { api, formatApiError } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { PencilSimple, FloppyDisk, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const ClientInfoTab = ({ client, onClientUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const startEditing = () => {
    setEditForm({
      name: client?.name || '',
      email: client?.email || '',
      phone: client?.phone || '',
      company_name: client?.company_name || '',
      address: client?.address || '',
      tax_id: client?.tax_id || '',
    });
    setEditing(true);
  };

  const saveClientDetails = async () => {
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/clients/${client.id}`, editForm);
      onClientUpdate(data);
      setEditing(false);
      toast.success('Client details updated');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSavingEdit(false);
    }
  };

  const fields = [
    { key: 'name', label: 'Full Name' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'phone', label: 'Phone' },
    { key: 'company_name', label: 'Company Name' },
    { key: 'address', label: 'Address' },
    { key: 'tax_id', label: 'Tax ID' },
  ];

  return (
    <Card className="border border-border">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="heading-4">Client Information</CardTitle>
        {!editing ? (
          <Button variant="outline" size="sm" onClick={startEditing} data-testid="edit-client-btn">
            <PencilSimple className="w-4 h-4 mr-1" /> Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={saveClientDetails} disabled={savingEdit} data-testid="save-client-btn">
              <FloppyDisk className="w-4 h-4 mr-1" /> {savingEdit ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map(({ key, label, type }) =>
            editing ? (
              <div key={key} className="space-y-1">
                <Label className="text-muted-foreground text-sm">{label}</Label>
                <Input
                  type={type || 'text'}
                  value={editForm[key] || ''}
                  onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                  data-testid={`edit-${key.replace('_', '-')}`}
                />
              </div>
            ) : (
              <div key={key}>
                <Label className="text-muted-foreground text-sm">{label}</Label>
                <p className="font-medium">{client[key] || '-'}</p>
              </div>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
};
